import {Injectable} from "@angular/core";
import {Http, Response} from "@angular/http";
import {Observable} from "rxjs/Observable";
import "rxjs/add/operator/map";
import "rxjs/add/operator/catch";
import {UtilService} from "../util/util.service";
import {SessionService} from "../session/session.service";
import {FacilityModel} from "../../models/location.model";

@Injectable()
export class AuthService {
  constructor(private http: Http, private utilService: UtilService,
              private sessionService: SessionService) {
  }

  private outputParser(res: Response) {
    let responseText: string = res.text();
    if (!responseText) {
      return null;
    }
    try {
      return JSON.parse(responseText);
    } catch (e) {
      return null;
    }
  }

  isAdmin() {
    const user = this.getUser();
    const validRoles = ["admin", "superadmin"];
    return validRoles.includes(user.Role);
  }

  authenticateAdmin(payload: LoginPayload) {
    const validRoles = ["admin", "phlebotomist"];

    return this.authenticate(payload, validRoles);
  }

  authenticatePhysician(payload: LoginPayload) {
    const validRoles = ["staff", "physician", "assistant", "phlebotomist", "admin", "delegate"];
    return this.authenticate(payload, validRoles);
  }

  authenticatePatient(payload: LoginPayload) {
    const validRoles = ["patient"];

    return this.authenticate(payload, validRoles);
  }

  refresh(facility: FacilityModel) {
    const data = {
      SystemId: facility.System.Id,
      Id: facility.Id,
      FacilityId: facility.Facility.Id
    };
    return new Promise((resolve, reject) => {
      this.http.post("/auth/refresh", data)
        .subscribe((resp: Response) => {
            let body = this.outputParser(resp);
            this.sessionService.setAccessToken(body.AccessToken);
            this.sessionService.setUser(body.User);
          },
          reject,
          resolve)

    })

  }

  private authenticate(payload: LoginPayload, validRoles: Array<string>) {
    return new Promise((resolve, reject) => {
      this.http.post("/auth/login", JSON.stringify(payload), {}).subscribe(
        (resp: Response) => {
          let body = this.outputParser(resp);

          this.sessionService.setAccessToken(body.AccessToken);
          this.sessionService.setUser(body.User);
          // TODO: Serhiy not sure how this will get handled in the backend but this is where
          // the phoneAuthNeeded storage item is needed to be set in order for the two-fa to be skipped

        },
        reject,
        resolve
      );
    });
  }

  /**
   * Method to take a received token string from a URL and send it
   * to the save new password endpoint.
   * @param token - Token string from URL
   * @param password - password provided by input
   * @param userId - userId string from URL
   * @returns {Promise<TokenPayload>}
   */
  submitPasswordReset(password: string,
                      userId: string): Promise<TokenPayload> {
    const url = "/users/" + userId + "/password";

    const payload: any = {
      Password: password
    };

    return new Promise((resolve, reject) => {
      this.http
        .post(url, JSON.stringify(payload), {})
        .subscribe((resp: Response) => {
          resolve(this.outputParser(resp));
        }, reject);
    });
  }

  changePassword(oldPassword, password: string,
                 userId: string): Promise<TokenPayload> {
    const url = "/users/" + userId + "/password";

    const payload: any = {
      NewPassword: password,
      OldPassword: oldPassword
    };

    return new Promise((resolve, reject) => {
      this.http
        .post(url, JSON.stringify(payload), {})
        .subscribe((resp: Response) => {
          resolve(this.outputParser(resp));
        }, reject);
    });
  }

  /**
   * Sends a reset password link to entered email if one exists on our system.
   *
   * @param accountEmail - Token string from URL
   * @returns {Observable<Response>}
   */
  sendResetPasswordEmail(accountEmail: string): Observable<Response> {
    const url = `/users/password`;
    return this.http.put(url, {
      username: accountEmail
    });
  }

  getUser(): User {
    return this.sessionService.getUser();
  }

  isDelegate() {
    return this.getUser().Role === 'delegate';
  }

  isPhysician() {
    return this.getUser().Role === 'physician';
  }

  getLastFacility() {
    const user = this.sessionService.getUser();
    return (<FacilityModel>user.LastFacility).Facility;
  }

  verifyDOB(payload: DOBPayload) {
    return new Promise((resolve, reject) => {
      let formattedDob = this.utilService.formatDate(
        payload.yearOfBirth,
        payload.monthOfBirth,
        Number(payload.dayOfBirth)
      );

      this.http
        .post(`/user/${payload.userId}/dob`, {dob: formattedDob})
        .subscribe(
          (res: Response) => {
            switch (res.statusText) {
              case "OK": {
                resolve(res.json());
                break;
              }
              case "Not Found": {
                reject(res);
                break;
              }
              default: {
                reject(res);
                break;
              }
            }
          },
          reject,
          resolve
        );
    });
  }

  registerPhone(phoneNumber: PhoneNumber) {
    return new Promise((resolve, reject) => {
      let {phoneOne: p1, phoneTwo: p2, phoneThree: p3} = phoneNumber;
      // Phone assumed to be US phones only
      // Prefixing with +1 country code
      let phoneString = `+1${p1}${p2}${p3}`;

      this.http
        .post(
          `/user/authenticate/register`,
          {PhoneNumber: phoneString},
          {}
        )
        .subscribe(
          (res: Response) => {
            if (res.status === 200 && res.statusText === "OK") {
              resolve({requestId: res.text(), phoneNumber: phoneNumber});
            } else {
              reject(res);
            }
          },
          reject,
          resolve
        );
    });
  }

  verifyPhone(codePin: string, requestId: string, skipVerification?: boolean, userId?: number) {
    return new Promise((resolve, reject) => {

      let _body: any = {
        PinNumber: codePin,
        RequestId: requestId
      };

      if (skipVerification) {
        _body.SkipVerification = true;
      }

      if (userId) {
        _body.UserId = userId;
      }

      this.http
        .post(`/user/authenticate/verify`, _body)
        .subscribe(
          (res: Response) => {
            resolve(res);
          },
          reject,
          resolve
        );
    });
  }

  loginTwoFA(PatientId: number) {
    return new Promise((resolve, reject) => {
      this.http
        .post("/user/authenticate/twofa", {id: PatientId}, {})
        .subscribe(
          (res: Response) => {
            if (res.status === 200 && res.statusText === "OK") {
              resolve(res.json());
            } else {
              reject(res);
            }
          },
          reject,
          resolve
        );
    });
  }

  patientPasswordReset(email: string): Promise<string> {
    let params: URLSearchParams = new URLSearchParams();
    params.set("email", email);
    return new Promise((resolve, reject) => {
      this.http.get(`/user/password?email=${email}`).subscribe(
        (res: any) => {
          resolve(res);
        },
        reject,
        resolve
      );
    });
  }
}



// WEBPACK FOOTER //
// /Users/maxsheikhizadeh/Desktop/GEN/physician-portal/src/app/shared/services/auth/auth.service.ts